/// <reference types="vite/client" />
/**
 * API client. Talks to universalbench-mcp on Railway. Token comes from URL query
 * (`?t=<jwt>`) minted by video_edit.py on render completion.
 *
 * If USE_MOCK is flipped back on, falls through to fixtures for offline dev.
 */

import type { VideoProject } from './types'
import { getMockProject } from '../mock/fixtures'
import { MockEventSource } from '../mock/mockSSE'

const USE_MOCK = false
const API_BASE = (import.meta.env.VITE_API_BASE || 'https://universalbench-mcp-production.up.railway.app').replace(/\/$/, '')

/** Extract the `t` (token) query parameter from the current URL. */
function getTokenFromUrl(): string {
  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search)
  return params.get('t') || ''
}

export async function fetchProject(projectId: string): Promise<VideoProject | null> {
  if (USE_MOCK) {
    await new Promise((r) => setTimeout(r, 150))
    return getMockProject(projectId)
  }

  const token = getTokenFromUrl()
  const url = `${API_BASE}/api/v/${encodeURIComponent(projectId)}?t=${encodeURIComponent(token)}`
  const res = await fetch(url, { credentials: 'omit' })
  if (res.status === 404) return null
  if (res.status === 401) throw new Error('Link expired or invalid. Ask Claude for a fresh link.')
  if (res.status === 403) throw new Error('Access denied. This link is for a different project.')
  if (!res.ok) throw new Error(`Failed to load project: HTTP ${res.status}`)
  return res.json()
}

/**
 * Open a stream for a project. Polls fetchProject every 2s until terminal or 5 min.
 */
export function openProjectStream(projectId: string): EventSource {
  if (USE_MOCK) {
    return new MockEventSource(projectId) as unknown as EventSource
  }
  return new PollingEventSource(projectId) as unknown as EventSource
}

class PollingEventSource {
  private timer: number | null = null
  private closed = false
  public onmessage: ((ev: MessageEvent) => void) | null = null
  public onerror: (() => void) | null = null
  private lastStatus: string | null = null
  private tickCount = 0

  constructor(private projectId: string) {
    this.tick()
  }

  private async tick() {
    if (this.closed) return
    this.tickCount++
    try {
      const p = await fetchProject(this.projectId)
      if (p && p.versions.length > 0) {
        const v = p.versions[p.versions.length - 1]
        if (v.status !== this.lastStatus) {
          this.lastStatus = v.status
          if (v.status === 'ready' && this.onmessage) {
            this.onmessage(new MessageEvent('message', {
              data: JSON.stringify({
                event: 'version_ready',
                version: v.version,
                output_url: v.output_url || '',
                duration_seconds: v.duration_seconds || 0,
                timestamp: v.ready_at || new Date().toISOString(),
              })
            }))
          }
        }
        if (v.status === 'ready' || v.status === 'failed') {
          this.close()
          return
        }
      }
    } catch (e) {
      if (this.onerror) this.onerror()
    }
    if (this.tickCount > 150) {
      this.close()
      return
    }
    this.timer = window.setTimeout(() => this.tick(), 2000)
  }

  close() {
    this.closed = true
    if (this.timer !== null) {
      window.clearTimeout(this.timer)
      this.timer = null
    }
  }
}
